import { useHover } from '@react-aria/interactions'
import { useObjectRef } from '@react-aria/utils'
import cx from 'classnames'
import { CSSProperties, forwardRef } from 'react'
import { matchPath, NavLink, useLocation } from 'react-router-dom'
import { ExpandButton } from './ExpandButton/ExpandButton'
import Styles from './NavItem.module.scss'
import { NavItemContext } from './NavItemContext'
import { NavItemProps } from './Types'

const Link = forwardRef<any, NavItemProps>((props, fRef) => {
  let {
    isHighlighted,
    children,
    indentLevel = 0,
    onPress = () => {},
    elementType: Component = NavLink,
    to,
    target,
    classNames,
    noMargin = false,
    ...rest
  } = props
  const location = useLocation()
  const ref = useObjectRef(fRef)
  let { hoverProps, isHovered } = useHover({})
  hoverProps = {
    ...hoverProps,
    onMouseMove: hoverProps.onPointerEnter,
  }
  if (!to) {
    Component = 'div'
  }

  const baseProps = {
    ...hoverProps,
    children,
    onClick: onPress,
    style: {
      '--indent-level': indentLevel,
    } as CSSProperties,
  }

  if (Component === 'a') {
    return (
      <NavItemContext.Provider value={{ isActive: !!rest.isActive, isHovered, ...hoverProps }}>
        <Component
          {...baseProps}
          data-testid={rest['data-testid']}
          target={target}
          ref={ref}
          href={typeof to === 'string' ? to : to?.pathname}
          className={cx(Styles.root, classNames, {
            [Styles.active]: rest.isActive && !isHighlighted,
            [Styles.highlighted]: isHighlighted,
            [Styles.modifiedActiveColors]: rest.modifiedActiveColors,
            [Styles.noMargin]: noMargin,
          })}
        />
      </NavItemContext.Provider>
    )
  }

  if (Component === NavLink) {
    const isActive =
      rest.isActive ??
      Boolean(
        matchPath(location.pathname, {
          path: typeof to === 'string' ? to : to?.pathname,
          exact: rest.exact ?? false,
        }),
      )

    return (
      <NavItemContext.Provider value={{ isActive, isHovered, ...hoverProps }}>
        <NavLink
          {...baseProps}
          data-testid={rest['data-testid']}
          target={target}
          ref={ref}
          to={to || ''}
          className={cx(Styles.root, classNames, {
            [Styles.active]: isActive && !isHighlighted,
            [Styles.highlighted]: isHighlighted,
            [Styles.modifiedActiveColors]: rest.modifiedActiveColors,
            [Styles.noMargin]: noMargin,
          })}
        />
      </NavItemContext.Provider>
    )
  }

  return (
    <NavItemContext.Provider value={{ isActive: !!rest.isActive, isHovered, ...hoverProps }}>
      <div
        {...baseProps}
        data-testid={rest['data-testid']}
        ref={ref}
        className={cx(Styles.root, classNames, {
          [Styles.active]: rest.isActive && !isHighlighted,
          [Styles.highlighted]: isHighlighted,
          [Styles.noMargin]: noMargin,
        })}
      />
    </NavItemContext.Provider>
  )
})

const NavItem = forwardRef<any, NavItemProps>(function NavItem(
  { children, isExpandable = false, isExpanded = false, onExpand = () => {}, padLeft = true, ...props },
  fRef,
) {
  return (
    <Link ref={fRef} {...props}>
      <div className={cx(Styles.left, { [Styles.padded]: padLeft })}>
        {isExpandable && <ExpandButton isExpanded={isExpanded} onPress={onExpand} />}
      </div>
      {children}
    </Link>
  )
})

export { NavItem }
